/* eslint-disable camelcase */
import jsrsasign from 'jsrsasign';
import { TypeAction, TypeOperate } from '@byted/tiktok-zti-types';
import {
  logSetStorage,
  logGetStorage,
  logStorageFail,
  logJsrsasignPerformance
} from '@byted/tiktok-zti-monitor';
/**
 * @param data string
 * @returns base64 string
 */
export const convertStringToBase64 = (data: string) => {
  const res = jsrsasign.utf8tob64(data);
  return res;
};

// string MD5
export const convertStringToMd5 = (data: string) => {
  const md = new jsrsasign.KJUR.crypto.MessageDigest({ alg: 'md5', prov: 'cryptojs' });
  md.updateString(data);
  const res = md.digest();

  return res;
};

/**
 * @param data hex string
 * @returns base64 string
 */
export const convertHexToBase64 = (data: string) => {
  const res = jsrsasign.hextob64(data);
  return res;
};

/**
 * @param data base64 string
 * @returns string
 */
export const convertBase64ToString = (data: string) => {
  const res = jsrsasign.b64toutf8(data);
  return res;
};

export const getPublicKeyFromCert = (cert: string) => {
  const key = jsrsasign.KEYUTIL.getKey(cert);
  const publicKey = jsrsasign.KEYUTIL.getPEM(key);
  return publicKey;
};

/**
 * hexto字节码
 * @param hex
 * @returns
 */
export function hexToBytes(hex: string) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}

/**
 * 字节码转base64
 * @param bytes
 * @returns
 */
export const bytesToBase64 = function (bytes: Uint8Array) {
  // 默认参数
  const base64EncodeChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  const base64 = [];
  for (let i = 0; i < bytes.length; i += 3) {
    const triplet = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    for (let j = 0; j < 4; j++) {
      if (i * 8 + j * 6 <= bytes.length * 8) {
        base64.push(base64EncodeChars.charAt((triplet >>> (6 * (3 - j))) & 0x3f));
      } else {
        base64.push('=');
      }
    }
  }
  return base64.join('');
};

const cachedPubKeyBase64: Record<string, string> = {};

/**
 * 通过私钥获取公钥base64
 * 先hex => 字节码 => base64
 * @param priKey 私钥
 */
export const getPubKeyBase64 = (priKey: string) => {
  if (priKey) {
    const start = Date.now();
    if (!cachedPubKeyBase64[priKey]) {
      const rsa = jsrsasign.KEYUTIL.getKey(priKey);
      const pubKeyHexFromPri = (rsa as any).generatePublicKeyHex();
      const bytes = hexToBytes(pubKeyHexFromPri);
      const base64string = bytesToBase64(bytes);
      cachedPubKeyBase64[priKey] = base64string;
    }
    logJsrsasignPerformance({
      duration: Date.now() - start,
      type: 'getPubKeyBase64'
    });
    return cachedPubKeyBase64[priKey];
  }
  return '';
};

export const comparePublicWithCert = (ec_publicKey?: string, cert?: string) => {
  try {
    if (!ec_publicKey || !cert || typeof ec_publicKey !== 'string') {
      return false;
    }
    const publicKey = getPublicKeyFromCert(cert);
    if (publicKey === ec_publicKey) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const comparePublicKeyWithCert = (cryptData?: string, cert?: string) => {
  try {
    if (!cryptData || !cert || typeof cryptData !== 'string') {
      return false;
    }
    const cryptObject = JSON.parse(cryptData);
    const publicKey = getPublicKeyFromCert(cert);
    const { ec_publicKey } = cryptObject || {};
    if (publicKey === ec_publicKey) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const comparePublicKeyWithNewClient = (cryptData?: string, cert?: string) => {
  try {
    if (!cryptData || !cert || typeof cryptData !== 'string') {
      return false;
    }
    const cryptObject = JSON.parse(cryptData);
    const { ec_privateKey } = cryptObject || {};
    const publicKey = getPubKeyBase64(ec_privateKey);
    const newPublickKey = cert.split('.')[1];
    if (publicKey === newPublickKey) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const compareCsrWithCryptData = (cryptData?: string, publicKey?: string) => {
  try {
    if (!cryptData || !publicKey || typeof cryptData !== 'string') {
      return false;
    }
    const cryptObject = JSON.parse(cryptData);
    const { ec_publicKey } = cryptObject || {};
    if (ec_publicKey === publicKey) {
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const compareAllKeysInfoMD5 = (
  cryptCookie?: string,
  cryptData?: string,
  cert?: string,
  serverData?: string
) => {
  try {
    if (
      !cryptCookie ||
      !cryptData ||
      !cert ||
      !serverData ||
      typeof cryptData !== 'string' ||
      typeof serverData !== 'string'
    ) {
      return false;
    }
    const cryptObject = JSON.parse(cryptData || '{}');
    const { ec_publicKey } = cryptObject || {};
    if (!ec_publicKey) {
      return false;
    }
    const md5Obj = {
      publicKey: ec_publicKey,
      cert,
      serverData
    };
    const md5String = convertStringToMd5(JSON.stringify(md5Obj));
    if (md5String === cryptCookie) {
      return true;
    }
  } catch (error) {}
  return false;
};

export const generateKeysInfoMD5 = (cryptData?: string, cert?: string, serverData?: string) => {
  try {
    if (
      !cryptData ||
      !cert ||
      !serverData ||
      typeof cryptData !== 'string' ||
      typeof serverData !== 'string'
    ) {
      return '';
    }
    const cryptObject = JSON.parse(cryptData);
    const { ec_publicKey } = cryptObject || {};
    if (!ec_publicKey) {
      return '';
    }
    const md5Obj = {
      publicKey: ec_publicKey,
      cert,
      serverData
    };
    const md5String = convertStringToMd5(JSON.stringify(md5Obj));
    return md5String;
  } catch (error) {}
  return '';
};

export const reportAsyncThunk =
  <T extends (...arg: any) => any>(
    fn: T,
    content: string,
    reportError: (
      error: unknown,
      name: string,
      action?: TypeAction,
      type?: TypeOperate,
      params?: any
    ) => void,
    type?: TypeOperate,
    action?: TypeAction,
    storage_type?: string
  ) =>
  async (...params: Parameters<T>): Promise<Awaited<ReturnType<T>> | undefined> => {
    try {
      const start = Date.now();
      const res = await fn.apply(this, params);
      const end = Date.now();
      let key = params?.[0] || '';
      if (Array.isArray(key)) {
        key = key.join(',');
      }
      if (type?.startsWith('get')) {
        logGetStorage({
          key,
          duration: end - start,
          storage_type: storage_type || ''
        });
        if (!res) {
          logStorageFail({
            message: 'get null',
            type: storage_type,
            action,
            category: type?.startsWith('get') ? 'get' : 'set'
          });
        }
      } else if (type?.startsWith('set')) {
        logSetStorage({
          key,
          duration: end - start,
          storage_type: storage_type || ''
        });
      }
      return res;
    } catch (e) {
      logStorageFail({
        message: (e as Error)?.message,
        type: storage_type,
        action,
        category: type?.startsWith('get') ? 'get' : 'set'
      });

      reportError?.(e, content, action, type, params);
    }
  };

export const checkEnv = () => {
  const ua = window?.navigator.userAgent;
  const regex = /TTElectron/;
  if (regex.test(ua)) {
    return 'electron';
  }
  return 'web';
};

/**
 * 缓存结果的pipeline，只会执行一次
 * 如果返回为false，则会下次会重新执行
 * 扩展运算法的声明还得想想
 * @param fn
 * @returns
 */
export const pipeline = <T>(fn: (...params: any) => Promise<T>) => {
  let result: T, startedPromise: Promise<T> | undefined;

  return (...params: any): Promise<T> => {
    if (result) {
      return Promise.resolve(result);
    }

    if (!startedPromise) {
      startedPromise = new Promise((resolve, reject) => {
        fn(...params)
          .then((res) => {
            res && (result = res);
            resolve(res);
            startedPromise = undefined;
          })
          .catch((error) => {
            reject(error);
            startedPromise = undefined;
          });
      });
    }
    return startedPromise;
  };
};

/**
 * 不缓存结果的pipeline，当之前一个任务没有完成之前，会pending所有的promise
 * @param fn
 * @returns
 */
export const purePipeline = <T>(fn: (...params: any) => Promise<T>) => {
  let startedPromise: Promise<T> | undefined;

  return (...params: any): Promise<T> => {
    if (!startedPromise) {
      startedPromise = new Promise((resolve, reject) => {
        fn(...params)
          .then((res) => {
            resolve(res);
          })
          .catch((error) => reject(error))
          .finally(() => {
            startedPromise = undefined;
          });
      });
    }

    return startedPromise;
  };
};

/**
 * @function
 * @description  Promise 失败自动重试，重试次数times。
 * @param {() => Promise<T>} fn - Promise
 * @param {times} times - 重试次数
 * @returns {Promise<never> | Promise<T>} - 返回异步执行结果。
 * @template T
 */
export const autoRetryPromise = <T>(fn: () => Promise<T>, times = 5): Promise<T> =>
  new Promise((resolve, reject) => {
    const attempt = () => {
      fn()
        .then((res) => {
          resolve(res);
        })
        .catch((error) => {
          // eslint-disable-next-line no-param-reassign
          times-- > 0 ? attempt() : reject(error);
        });
    };
    attempt();
  });

/**
 * promise队列执行，可用用于多个函数相互等待的场景
 * 此处如果换成观察者，好像不太行
 */
export class Scheduler {
  list: any = [];
  count = 0;
  maxCount = 1;
  constructor() {
    this.list = [];
    this.count = 0;
  }

  // 添加任务队列
  addTask(promiseCreator: () => Promise<any>) {
    return new Promise((resolve) => {
      // 添加的时候通知 队列有东西可以消费
      this.list.push(() =>
        promiseCreator().then((res) => {
          this.count -= 1;
          // 一个任务做完下一个任务才开始
          this.consume();
          resolve(res);
          return res;
        })
      );
      this.consume();
    });
  }

  provider = (fn: (...params: any) => Promise<any>) =>
    this.addTask(
      () =>
        new Promise((resolve, reject) => {
          fn()
            .then((res) => {
              resolve(res);
            })
            .catch((err) => {
              reject(err);
            });
        })
    );

  // 等待前序队列全部执行完毕，阻塞
  wait = () => {
    this.provider(() => Promise.resolve(true));
  };

  consume() {
    if (this.count < this.maxCount && this.list.length > 0) {
      this.count += 1;
      const item = this.list.shift();
      item && item();
    }
  }
}
